import {
  getClipsForProject,
  getProjectAudio,
  getProjectDocumentRecords,
  getProjectText,
  listProjects,
} from "./storage";
import { projectBackupFilename, serializeProject } from "./project-transfer";
import { projectKind } from "./types";
import type { Project, ProjectId } from "./types";

/**
 * Taking everything off one install, in one go.
 *
 * This exists for the move to a single app: a project has only ever lived in
 * the browser that recorded it, and a browser's storage is per-origin, so the
 * only way projects reach a new domain is a file the user carries. Backing them
 * up one at a time is possible today and nobody with eleven projects will do
 * it.
 *
 * ONE FILE PER PROJECT, deliberately, not one archive of all of them. A project
 * with a few clips runs to hundreds of megabytes, so "everything" is a
 * multi-gigabyte write that fails at ninety percent with nothing to retry from
 * and nothing to show for it. Per project, a failure costs one project and the
 * rest are already on disk.
 */

/** One project, packed. */
export interface ProjectBackup {
  projectId: ProjectId;
  projectName: string;
  filename: string;
  blob: Blob;
}

/**
 * Read a project out of storage and pack it.
 *
 * Shared by the single-project backup and the export-everything run, so the two
 * cannot drift — a fix to what a backup contains has one home.
 */
export async function buildProjectBackup(
  project: Project,
): Promise<ProjectBackup> {
  const clips = await getClipsForProject(project.id);
  const audio = await getProjectAudio(project.id);
  // A video project's notes live on its elements; a voice or text project's
  // body is the project itself, and travels with the audio so a restore is
  // complete.
  const body =
    projectKind(project) === "video"
      ? ""
      : ((await getProjectText(project.id))?.body ?? "");
  const { sections, elements } = await getProjectDocumentRecords(project.id);
  return {
    projectId: project.id,
    projectName: project.name,
    filename: projectBackupFilename(project.name),
    blob: serializeProject(project, clips, audio, body, { sections, elements }),
  };
}

/** What happened to one project in a run. */
export interface ExportOutcome {
  projectName: string;
  bytes: number;
  error?: string;
}

export interface ExportEverythingResult {
  done: ExportOutcome[];
  failed: ExportOutcome[];
}

/**
 * Pack and hand over every project on this install.
 *
 * `deliver` does whatever the caller does with a file — download it, share it,
 * write it somewhere. Kept out of here because this module must not touch the
 * DOM, and because a test can deliver into an array.
 *
 * A project that fails is RECORDED AND SKIPPED rather than aborting the run.
 * The whole point is to get everything off a device that is about to be retired,
 * and one unreadable clip must not strand the other ten projects — the result
 * says which ones to go back for.
 */
export async function exportEveryProject(
  deliver: (backup: ProjectBackup) => Promise<void>,
  onProgress?: (done: number, total: number, projectName: string) => void,
): Promise<ExportEverythingResult> {
  const projects = await listProjects();
  const done: ExportOutcome[] = [];
  const failed: ExportOutcome[] = [];
  let index = 0;
  for (const project of projects) {
    index += 1;
    onProgress?.(index, projects.length, project.name);
    try {
      const backup = await buildProjectBackup(project);
      // An empty project is not a failure — there is simply nothing to carry.
      if (backup.blob.size === 0) continue;
      await deliver(backup);
      done.push({ projectName: project.name, bytes: backup.blob.size });
    } catch (err) {
      failed.push({
        projectName: project.name,
        bytes: 0,
        error: err instanceof Error ? err.message : "Could not pack it",
      });
    }
  }
  return { done, failed };
}

/** The line shown when a run finishes. Plain counts, and the failures named. */
export function exportEverythingSummary(
  result: ExportEverythingResult,
): string {
  const { done, failed } = result;
  if (done.length === 0 && failed.length === 0) {
    return "There are no projects on this device to export.";
  }
  const saved =
    done.length === 1 ? "1 project saved" : `${done.length} projects saved`;
  if (failed.length === 0) {
    return `${saved} to your downloads. Keep the files — they are the only copy.`;
  }
  // Named, not counted: "2 failed" tells someone to go looking and not where.
  const names = failed.map((entry) => entry.projectName).join(", ");
  return `${saved}; could not pack ${names}. Try those again before retiring this install.`;
}
